import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
} from '@mui/material'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../app/hooks/useNotificationStack'
import {
  useCreateCustomerDocumentTypeMutation,
  useCreateTenantDocumentTypeMutation,
  useGetAllDocumentCategoriesQuery,
  useUpdateCustomerDocumentTypeMutation,
  useUpdateTenantDocumentTypeMutation,
} from '../../app/redux-fetch/apiCoreQuery'
import { useGetCustomersQuery } from '../../app/redux-fetch/apiQuery'
import DefaultSpacingGrid from '../../components/Containers/DefaultSpacingGrid'
import FormInput from '../../components/Forms/FormInput'
import FormSelect from '../../components/Forms/FormSelect'
import SideModalWrapper from '../Assets/modals/SideModalWrapper'
import {
  DocumentTypeFormValues,
  documentTypeSchema
} from './DocumentTypeValidationSchema'
import { messages } from './messages'
import { IDocumentType } from './types'

type DocumentTypeFormModalProps = {
  isOpen: boolean
  documentTypeToEdit?: IDocumentType
  handleSubmit?: (documentType: IDocumentType) => void
  handleCancel: () => void
}

const DocumentTypeFormModal = ({
  documentTypeToEdit,
  isOpen,
  handleSubmit: handleClose,
  handleCancel,
}: DocumentTypeFormModalProps) => {
  const { formatMessage } = useIntl()
  const { enqueueSuccess, enqueueFailure } = useNotificationStack()

  const { tenantId, customerId, isTenantUser } = useLoggedInUser()

  const isNew = !documentTypeToEdit || !documentTypeToEdit.id

  const methods = useForm<DocumentTypeFormValues>({
    defaultValues: {
      name: documentTypeToEdit?.name || '',
      customerId: documentTypeToEdit?.customerId || customerId || undefined,
      tenantId: documentTypeToEdit?.tenantId ?? tenantId,
      documentCategoryId: documentTypeToEdit?.documentCategoryId,
      id: documentTypeToEdit?.id,
      enabled: documentTypeToEdit?.enabled ?? true,
    },
    mode: 'all',
    resolver: zodResolver(documentTypeSchema)
  })
  const { setValue, control } = methods

  const [createTenantDocType, { isLoading: isCreatingTenant }] =
    useCreateTenantDocumentTypeMutation()
  const [createCustomerDocType, { isLoading: isCreatingCustomer }] =
    useCreateCustomerDocumentTypeMutation()
  const [updateTenantDocType, { isLoading: isUpdatingTenant }] =
    useUpdateTenantDocumentTypeMutation()
  const [updateCustomerDocType, { isLoading: isUpdatingCustomer }] =
    useUpdateCustomerDocumentTypeMutation()

  const isSubmitting =
    isCreatingTenant || isCreatingCustomer || isUpdatingTenant || isUpdatingCustomer
  const isDisabled = !handleClose || isSubmitting

  const { data: documentCategories } = useGetAllDocumentCategoriesQuery()
  const { data: customers = [] } = useGetCustomersQuery(tenantId ?? -1, {
    skip: !tenantId || !isTenantUser,
  })

  const handleFormSubmit = methods.handleSubmit(async (formData: DocumentTypeFormValues) => {
    try {

      const effectiveTenantId = formData.tenantId || tenantId;
      formData.tenantId = effectiveTenantId;
      // Normalize: treat customerId 0 as undefined (no customer selected)
      formData.customerId = formData.customerId || undefined;
      const effectiveCustomerId = formData.customerId;

      if (!isNew) {
        if (effectiveCustomerId) {
          await updateCustomerDocType({
            id: formData.id,
            customerId: effectiveCustomerId,
            documentType: formData,
          }).unwrap()
        } else {
          await updateTenantDocType({
            tenantId,
            id: formData.id,
            documentType: formData,
          }).unwrap()
        }
      } else {
        if (effectiveCustomerId) {
          await createCustomerDocType({
            customerId: effectiveCustomerId,
            documentType: formData,
          }).unwrap()
        } else {
          await createTenantDocType({
            tenantId,
            documentType: formData,
          }).unwrap()
        }
      }
      enqueueSuccess(isNew ? messages.documentTypeCreated : messages.documentTypeUpdated);

      methods.reset({
        name: '',
        customerId: undefined,
        documentCategoryId: undefined
      });

      handleClose && handleClose(formData as IDocumentType);
    } catch (error) {
      console.error('Error saving document type:', error);
      enqueueFailure(isNew ? messages.documentTypeNotCreated : messages.documentTypeNotUpdated);
    }
  });


  const handleCancelBtn = () => {
    methods.reset({
      name: '',
      customerId: undefined,
      documentCategoryId: undefined
    });
    handleCancel();
  }

  return (
    <SideModalWrapper
      open={isOpen}
      onClose={handleCancelBtn}
      headerLabel={
        documentTypeToEdit?.id
          ? formatMessage(messages.editDocumentType)
          : formatMessage(messages.newDocumentType)
      }
    >
      <FormProvider {...methods}>
        <form onSubmit={handleFormSubmit}>
          <Box sx={{ minWidth: '450px', m: '32px' }}>
            <FormInput
              name={'name'}
              label={formatMessage(messages.documentTypeName)}
              placeholder={formatMessage(messages.documentTypeName)}
              fullWidth
              required
            />
            {isTenantUser && (
              <FormSelect
                name="customerId"
                label={formatMessage(messages.customer)}
                options={(customers ?? []).map(customer => ({
                  value: customer.id,
                  label: customer.name,
                }))}
                disableLabel={formatMessage(messages.noCustomersAvailable)}
                placeholder={formatMessage(messages.selectCustomer)}
                actionLabel={
                  <Button
                    size='small'
                    onClick={() => setValue('customerId', undefined)}
                    sx={{ textTransform: 'none', p: 0, mb: '6px' }}
                    disabled={isDisabled}
                  >
                    {formatMessage(messages.clear)}
                  </Button>
                }
                fullWidth
                disabled={isDisabled}
              />
            )}
            <FormSelect
              name="documentCategoryId"
              label={formatMessage(messages.documentCategory)}
              options={(documentCategories ?? []).map(category => ({
                value: category.id,
                label: category.name,
              }))}
              disableLabel={formatMessage(messages.noDocumentCategoriesAvailable)}
              placeholder={formatMessage(messages.selectDocumentCategory)}
              actionLabel={
                <Button
                  size='small'
                  onClick={() => setValue('documentCategoryId', null)}
                  sx={{ textTransform: 'none', p: 0, mb: '6px' }}
                  disabled={isDisabled}
                >
                  {formatMessage(messages.clear)}
                </Button>
              }
              fullWidth
              disabled={isDisabled}
              required
            />
            <Controller
              name={'enabled'}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={formatMessage(messages.enabled)}
                />
              )}
            />
          </Box>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              width: '100%',
              padding: '16px',
              backgroundColor: '#fff',
              borderTop: '1px solid #e0e0e0',
              zIndex: 999,
            }}
          >
            <DefaultSpacingGrid container justifyContent='flex-start' sx={{ mt: 0 }}>
              <Grid item>
                <Button variant='outlined' type='button' onClick={handleCancelBtn}>
                  {formatMessage(messages.cancelBtn)}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant='contained'
                  type='submit'
                  disabled={isSubmitting}
                >
                  {isNew
                    ? formatMessage(messages.createBtn)
                    : formatMessage(messages.updateBtn)}
                </Button>
              </Grid>
            </DefaultSpacingGrid>
          </Box>
        </form>
      </FormProvider>
    </SideModalWrapper>
  )
}

export default DocumentTypeFormModal
